import React, { useEffect, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'

import { Box, Button, Flex, HStack, Heading, Image, InputLeftAddon, Link, Text } from '@chakra-ui/react'
import { FiLock, FiUser } from 'react-icons/fi'

import { Input } from '@/components/Form/'
import { authSelector, loginWithEmail, loginWithSiemensID } from '@/stores/auth/authSlice'

//Hooks
import { hideLoadingOverlay, showLoadingOverlay } from '@/stores/ui/loadingOverlaySlice'

//Constant
import config, { APP_MODE } from '@/config'
import storage from '@/utils/storage'
import { camelToWords } from '@/utils/stringHelper'

const LoginForm = () => {
  const isProduction = config.mode === APP_MODE.PRODUCTION
  const dispatch = useDispatch()
  const methods = useForm()
  const location = useLocation()

  //direct approve redirect and show message

  const { isLoading } = useSelector(authSelector)
  const [isLoadingLoginWithSiemensID, setIsLoadingLoginWithSiemensID] = useState(false)
  const hasTriggeredSiemensLoginRef = useRef(false)

  useEffect(() => {
    const loginCode = new URLSearchParams(location.search).get('code')
    if (!loginCode || hasTriggeredSiemensLoginRef.current) return

    hasTriggeredSiemensLoginRef.current = true

    try {
      dispatch(showLoadingOverlay())
      dispatch(loginWithSiemensID({ code: loginCode }))
    } catch (err) {
      console.log(err)
    } finally {
      setTimeout(() => {
        dispatch(hideLoadingOverlay())
      }, 10000)
    }
  }, [dispatch, location.search])

  const onSubmit = (data) => {
    //TODO: Change to load config from server
    if (
      ![APP_MODE.DEVELOPMENT, APP_MODE.STAGING, APP_MODE.PREPROD].includes(config.mode) &&
      data.email.includes('siemens')
    ) {
      toast.error('Email login is disabled in production for Siemens user, please use Siemens Login instead ! ')
      return
    }

    //Set Redirect
    if (location?.state?.from) {
      const redirectPath =
        typeof location.state.from === 'string' ? location.state.from : location.state.from?.pathname || '/home'

      storage.setRedirect(redirectPath)
    }

    dispatch(loginWithEmail(data))
  }

  const handleLoginWithSiemensID = async () => {
    try {
      setIsLoadingLoginWithSiemensID(true)
      dispatch(showLoadingOverlay())

      const siemensIdLoginOptions = {
        client_id: config.siemensIdLoginClientId,
        redirect_uri: config.siemensIdLoginRedirectUrl,
        connection: 'main-tenant-oidc',
        display:
          'amazon-off|apple-off|azure-social-common-off|azure-social-SAG-off|flender-off|github-off|gitlab-off|linkedin-off|an-Login_to_E-Approval',
        response_type: 'code',
        response_mode: 'query',
        scope: 'openid%20profile%20email',
        audience: 'siemensid_onboarding',
      }

      const paramString = new URLSearchParams(siemensIdLoginOptions).toString()

      console.log('paramString', paramString)

      window.location.replace(`${config.siemensIdLoginUrl}?${paramString}`)
    } catch (err) {
      console.log(err)
      toast.error('Login with Siemens ID failed.')
    } finally {
      setIsLoadingLoginWithSiemensID(false)
      dispatch(hideLoadingOverlay())
    }
  }

  const showReadMeFile = () => {
    window.open(`/README`, '_blank')
  }

  return (
    <Flex direction="column" bg="white" p={[6, 12]} minW={[0, 700]}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <Flex direction="column" gap="2">
            <Box mb="20px">
              <Image
                w="150px"
                objectFit="cover"
                src={
                  // eslint-disable-next-line max-len
                  'https://www.siemens-home.bsh-group.com.hk/store/medias/sys_master/root/h63/hcd/10563534323742/GL-SIE-Logo-Siemens-Petrol-RGB-BSH-Reshape-2021.png'
                }
              />
            </Box>
            {config.mode !== APP_MODE.PRODUCTION && <Heading size="sm">{camelToWords(config.mode)}</Heading>}

            <Text>Sign In to your e-approval account</Text>

            {!isProduction && (
              <>
                <Input
                  name="email"
                  type="email"
                  placeholder="Email"
                  canHighLight={false}
                  autoComplete={'email'}
                  leadingIcon={
                    <InputLeftAddon>
                      <FiUser />
                    </InputLeftAddon>
                  }
                />
                <Input
                  name="password"
                  type="password"
                  placeholder="Password"
                  canHighLight={false}
                  leadingIcon={
                    <InputLeftAddon>
                      <FiLock />
                    </InputLeftAddon>
                  }
                  autoComplete={'password'}
                />
                <Link color="teal" href="/auth/requestPasswordReset">
                  Forget Password ?
                </Link>
              </>
            )}

            <HStack>
              {!isProduction && (
                <Button isLoading={isLoading} loadingText="Logging In" colorScheme="blue" type="submit">
                  Login
                </Button>
              )}

              <Button
                isLoading={isLoadingLoginWithSiemensID}
                loadingText="Loading"
                colorScheme="teal"
                onClick={handleLoginWithSiemensID}
              >
                Login with Siemens ID
              </Button>
              <br />
            </HStack>
            <HStack>
              <Button loadingText="Loading" size="sm" colorScheme="blue" onClick={showReadMeFile}>
                Login Guide
              </Button>
            </HStack>
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  )
}

export default LoginForm
